import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { Monogram } from "./Logo";
import { useNav } from "./Header";
import { useSite, fullAddress, What3Words } from "@/lib/site";
import { openCookieSettings } from "./CookieConsent";

const LEGAL = [
  ["privacy", "Privacy Policy"], ["cookies", "Cookie Policy"], ["terms", "Website Terms"],
  ["vehicle-sales", "Vehicle Sales Terms"], ["reservations", "Reservation Terms"],
  ["complaints", "Complaints Procedure"], ["finance", "Finance Disclosure"],
];

export default function Footer() {
  const site = useSite();
  const nav = useNav();
  return (
    <footer data-testid="site-footer" className="on-dark grain bg-forest text-paper">
      <div className="relative z-[2] mx-auto max-w-[1440px] px-5 pb-10 pt-20 sm:px-8 lg:px-12">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-end gap-4">
              <Monogram className="h-14 w-14" light />
              <div>
                <p className="font-serif text-4xl leading-none">Cirencester <span className="font-semibold">4×4</span></p>
                <p className="eyebrow mt-2 text-stone">Specialist 4x4 &amp; SUV Sales</p>
              </div>
            </div>
            <p className="mt-10 font-serif text-3xl italic text-stone">Carefully selected. Properly prepared.</p>
          </div>
          <FooterCol title="Explore" span="lg:col-span-2">
            {nav.map((n) => <li key={n.to}><Link className="link-u" to={n.to}>{n.label}</Link></li>)}
            <li><Link className="link-u" to="/used/first-cars" data-testid="footer-first-cars">First cars</Link></li>
          </FooterCol>
          <FooterCol title="Visit" span="lg:col-span-3">
            <li data-testid="footer-address">{fullAddress(site)}<What3Words site={site} className="mt-1 text-stone" testid="footer-w3w" /></li>
            <li><a className="link-u" href={`tel:${site.phone_href}`} data-testid="footer-phone">{site.phone}</a></li>
            <li><a className="link-u" href={`mailto:${site.email}`}>{site.email}</a></li>
            <li><a className="link-u" href={site.directions_url} target="_blank" rel="noreferrer" data-testid="footer-directions">Get directions</a></li>
          </FooterCol>
          <FooterCol title="Opening hours" span="lg:col-span-3">
            {(site.opening_hours || []).map((h) => (
              <li key={h.day} className="flex justify-between gap-6"><span>{h.day}</span><span className="text-stone">{h.hours}</span></li>
            ))}
          </FooterCol>
        </div>
        <div className="mt-16 rule !bg-paper/15" />
        <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-[0.8rem] text-stone">
            {LEGAL.map(([slug, label]) => <li key={slug}><Link className="link-u" to={`/legal/${slug}`}>{label}</Link></li>)}
            <li><button className="link-u" onClick={openCookieSettings} data-testid="footer-cookie-settings">Cookie settings</button></li>
          </ul>
          <div className="flex gap-4">
            {[[Instagram, "instagram"], [Facebook, "facebook"], [Youtube, "youtube"]].map(([Icon, k]) => (
              <a key={k} href={site.socials?.[k] || "#"} aria-label={k} data-testid={`social-${k}`} className="grid h-10 w-10 place-items-center rounded-full border border-paper/30 transition-colors hover:bg-paper hover:text-forest">
                <Icon className="h-4 w-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>
        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-stone/90">{site.company_details}</p>
        <p className="mt-2 max-w-3xl text-xs leading-relaxed text-stone/90">{site.finance_disclosure}</p>
        <p className="mt-6 text-xs text-stone/90">© {new Date().getFullYear()} Cirencester 4x4. All rights reserved.</p>
      </div>
    </footer>
  );
}

function FooterCol({ title, children, span }) {
  return (
    <div className={span}>
      <p className="eyebrow mb-5 text-stone">{title}</p>
      <ul className="space-y-3 text-sm text-paper/90">{children}</ul>
    </div>
  );
}
